import React, { createContext, useContext, useState, useEffect } from 'react';
import { supportedLanguages, initializeI18n } from '../i18n';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isInitialized, setIsInitialized] = useState(false);
  const [i18nInstance, setI18nInstance] = useState(null);

  // Initialize i18n first
  useEffect(() => {
    const initI18n = async () => {
      try {
        await initializeI18n();
        // Get the i18n instance after initialization
        const { default: i18n } = await import('../i18n');
        setI18nInstance(i18n);
        
        // Initialize language from localStorage or browser preference
        const savedPrefs = JSON.parse(localStorage.getItem('appPreferences') || '{}');
        const savedLanguage = savedPrefs.language || i18n.language || 'en';
        
        // Ensure the language is supported
        const validLanguage = supportedLanguages[savedLanguage] ? savedLanguage : 'en';
        
        setCurrentLanguage(validLanguage);
        
        if (i18n.language !== validLanguage) {
          await i18n.changeLanguage(validLanguage);
        }
        
        setIsInitialized(true);
      } catch (error) {
        console.error('Error initializing i18n:', error);
        setIsInitialized(true); // Still set as initialized to prevent infinite loading
      }
    };

    initI18n();
  }, []);

  const changeLanguage = async (language) => {
    if (!supportedLanguages[language]) {
      console.warn(`Language ${language} is not supported`);
      return;
    }

    if (!i18nInstance) {
      console.warn('i18n not initialized yet');
      return;
    }

    try {
      await i18nInstance.changeLanguage(language);
      setCurrentLanguage(language);
      
      // Update preferences in localStorage
      const savedPrefs = JSON.parse(localStorage.getItem('appPreferences') || '{}');
      savedPrefs.language = language;
      localStorage.setItem('appPreferences', JSON.stringify(savedPrefs));
      
      console.log(`Language changed to: ${language}`);
    } catch (error) {
      console.error('Error changing language:', error);
    }
  };

  const getSupportedLanguages = () => {
    return Object.entries(supportedLanguages).map(([code, info]) => ({
      code,
      name: info.name,
      flag: info.flag
    }));
  };

  const value = {
    currentLanguage,
    changeLanguage,
    getSupportedLanguages,
    isInitialized,
    supportedLanguages,
    i18nInstance
  };

  // Show loading state while i18n is initializing
  if (!isInitialized) {
    return (
      <LanguageContext.Provider value={value}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%)'
        }}>
          <svg viewBox="0 0 24 24" fill="#ff6b35" style={{ width: '40px', height: '40px' }}>
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
            <path d="M12 4c4.41 0 8 3.59 8 8h2c0-5.52-4.48-10-10-10v2z">
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                from="0 12 12"
                to="360 12 12"
                dur="1s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
        </div>
      </LanguageContext.Provider>
    );
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Custom hook for flexible translation
export const useFlexibleTranslation = () => {
  const { currentLanguage } = useLanguage();
  const { t, i18n } = useTranslation();

  // Sync i18n with context language
  useEffect(() => {
    if (i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage);
    }
  }, [currentLanguage, i18n]);

  return {
    t,
    i18n,
    currentLanguage
  };
};